// 현장 콘솔 입력 모양 — 상태·계약 조건은 저장소와 transitions 가 본다.

import { z } from "zod";

import { MAX_SCHEDULE_REASON } from "./phase-schedule";
import { MAX_LOG_WEATHER, PROJECT_COMPLETE_NEXT, PROJECT_LOG_KINDS, PROJECT_PHASE_STATUSES } from "./types";
import { assertStartDate } from "../quote/schedule";

const DATE = /^\d{4}-\d{2}-\d{2}$/;

// 2-31 처럼 넘어가는 날짜까지 거른다 — 일정 생성기(`assertStartDate`)와 같은 자.
const isCalendarDate = (v: string): boolean => {
  try {
    assertStartDate(v);
    return true;
  } catch {
    return false;
  }
};

const baseRev = z
  .number({ message: "버전이 올바르지 않습니다" })
  .int({ message: "버전이 올바르지 않습니다" })
  .min(1, { message: "버전이 올바르지 않습니다" });

export const projectIdSchema = z.string({ message: "현장을 찾을 수 없습니다" }).min(1, { message: "현장을 찾을 수 없습니다" });

export const projectStartSchema = z.object({ base_rev: baseRev });

export const projectCompleteSchema = z.object({
  completion_date: z
    .string({ message: "준공일을 입력해 주세요" })
    .trim()
    .regex(DATE, { message: "준공일 형식이 올바르지 않습니다 (YYYY-MM-DD)" })
    .refine(isCalendarDate, { message: "없는 날짜입니다" }),
  next: z.enum(PROJECT_COMPLETE_NEXT, { message: "준공 뒤 상태를 골라 주세요" }),
  base_rev: baseRev,
});

export const projectCloseSchema = z.object({ base_rev: baseRev });

// 유지보수 종료일 — 준공일 이후인지는 액션이 `warrantyEndBlockReason` 으로 본다.
export const projectWarrantyEndSchema = z.object({
  warranty_end_date: z
    .string({ message: "유지보수 종료일을 입력해 주세요" })
    .trim()
    .regex(DATE, { message: "유지보수 종료일 형식이 올바르지 않습니다 (YYYY-MM-DD)" })
    .refine(isCalendarDate, { message: "없는 날짜입니다" }),
  base_rev: baseRev,
});

export const projectRescheduleSchema = z.object({
  start_date: z
    .string({ message: "착공일을 입력해 주세요" })
    .trim()
    .regex(DATE, { message: "착공일 형식이 올바르지 않습니다 (YYYY-MM-DD)" })
    .refine(isCalendarDate, { message: "없는 날짜입니다" }),
  base_rev: baseRev,
});

export const projectAssigneeSchema = z.object({
  assignee_id: z
    .string()
    .trim()
    .nullish()
    .transform((v) => (v ? v : null)),
  base_rev: baseRev,
});

// 일지·사진의 날짜 — 범위(착공일~오늘)는 액션이 `logDateBlockReason` 으로 본다.
export const logDateSchema = z
  .string({ message: "날짜를 입력해 주세요" })
  .trim()
  .regex(DATE, { message: "날짜 형식이 올바르지 않습니다 (YYYY-MM-DD)" })
  .refine(isCalendarDate, { message: "없는 날짜입니다" });

export const MAX_LOG_BODY = 2000;

export const PHASE_NOT_FOUND = "공정을 찾을 수 없습니다";

// 사진의 공정 태그 — 빈 값은 태그 없음(null). 인메모리 id 가 uuid 가 아니라 모양은 액션이 현장 공정 목록으로 가른다.
export const projectPhaseIdSchema = z
  .string({ message: PHASE_NOT_FOUND })
  .trim()
  .max(64, { message: PHASE_NOT_FOUND })
  .nullish()
  .transform((v) => (v ? v : null));

// 일지 투입 인원 — `0109` 의 crew_count check(1~99)와 같은 자 · 빈 값은 null.
export const MAX_LOG_CREW = 99;
const CREW_MESSAGE = `투입 인원은 1~${MAX_LOG_CREW}명으로 적어 주세요`;
export const logCrewCountSchema = z.preprocess(
  (v) => (v === "" || v === undefined ? null : v),
  z
    .number({ message: CREW_MESSAGE })
    .int({ message: CREW_MESSAGE })
    .min(1, { message: CREW_MESSAGE })
    .max(MAX_LOG_CREW, { message: CREW_MESSAGE })
    .nullable()
);

// 일지 날씨 — 앞뒤 공백을 털고 비면 null · 1~40자(`0128` project_log_weather_len).
export const logWeatherSchema = z
  .string({ message: "날씨가 올바르지 않습니다" })
  .nullish()
  .transform((v) => (v ?? "").trim())
  .refine((v) => Array.from(v).length <= MAX_LOG_WEATHER, { message: `날씨는 ${MAX_LOG_WEATHER}자까지 쓸 수 있습니다` })
  .transform((v) => (v === "" ? null : v));

export const projectLogSchema = z.object({
  log_date: logDateSchema,
  kind: z.enum(PROJECT_LOG_KINDS, { message: "구분을 골라 주세요" }),
  body: z
    .string({ message: "내용을 입력해 주세요" })
    .trim()
    .min(1, { message: "내용을 입력해 주세요" })
    .refine((v) => Array.from(v).length <= MAX_LOG_BODY, { message: `${MAX_LOG_BODY}자까지 쓸 수 있습니다` }),
  project_phase_id: projectPhaseIdSchema,
  crew_count: logCrewCountSchema,
  weather: logWeatherSchema,
});

const phaseStatus = z.enum(PROJECT_PHASE_STATUSES, { message: "공정 상태가 올바르지 않습니다" });

const scheduleReason = z
  .string({ message: "사유가 올바르지 않습니다" })
  .trim()
  .max(MAX_SCHEDULE_REASON, { message: `사유는 ${MAX_SCHEDULE_REASON}자까지 쓸 수 있습니다` });

export const projectPhaseStatusSchema = z.object({
  phase_id: z.string({ message: "공정을 찾을 수 없습니다" }).min(1, { message: "공정을 찾을 수 없습니다" }),
  from: phaseStatus,
  to: phaseStatus,
  // 완료 처리 때 지연·조기완료 사유 — 없으면 지금 사유를 그대로 둔다.
  reason: scheduleReason.optional(),
});

export const projectPhaseReasonSchema = z.object({
  phase_id: z.string({ message: "공정을 찾을 수 없습니다" }).min(1, { message: "공정을 찾을 수 없습니다" }),
  reason: scheduleReason,
});
